import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useUserStore } from '../../store/userStore';
import { getAuthToken, setAuthToken } from '../../utils/storage';

const axiosAdmin: AxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_ADMIN_API_URL}/api`,
  withCredentials: true,
});

axiosAdmin.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const accessToken = getAuthToken();
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  config.withCredentials = true;
  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  useUserStore.getState().clearUser();
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

axiosAdmin.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosAdmin(originalRequest));
            },
            reject: (error) => reject(error),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshRes = await axios.post(
          `${import.meta.env.VITE_SERVER_NESTJS_API_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const { accessToken: newToken } = refreshRes.data.data;
        setAuthToken(newToken);
        useUserStore.getState().setAccessToken(newToken);

        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return await axiosAdmin(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        handleLogout();
        return await Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else if (err.response?.status === 403) {
      processQueue(err.response, null);
      handleLogout();
      isRefreshing = false;
      return Promise.reject(err.response);
    }

    return Promise.reject(err);
  }
);

export default axiosAdmin;
